/**
 * «Ordenar el menú» (plan §5.11): the categories and each category's
 * products as id lists. Changes stay pending per list until «Guardar
 * orden»; nothing is sent while the owner drags.
 */
export type TReorderListKey = 'categories' | `cat:${number}`;

export type TReorderPending = Partial<Record<TReorderListKey, number[]>>;

export function productsListKey(categoryId: number): TReorderListKey {
  return `cat:${categoryId}`;
}

/** The category of a products list; null for the categories list. */
export function listCategoryId(key: TReorderListKey): number | null {
  if (key === 'categories') return null;
  const id = Number(key.slice(4));
  return Number.isInteger(id) ? id : null;
}

export function sameOrder(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/** `id` one step up (-1) or down (1); unchanged at the edges. */
export function moveStep(
  ids: readonly number[],
  id: number,
  delta: -1 | 1
): number[] {
  const index = ids.indexOf(id);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= ids.length) return [...ids];
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** «Al principio» / «Al final». */
export function moveToEdge(
  ids: readonly number[],
  id: number,
  edge: 'start' | 'end'
): number[] {
  if (!ids.includes(id)) return [...ids];
  const rest = ids.filter((candidate) => candidate !== id);
  return edge === 'start' ? [id, ...rest] : [...rest, id];
}

/**
 * The pending lists with this one set: a list back to its saved order
 * drops out (nothing to save).
 */
export function withPendingList(
  pending: TReorderPending,
  key: TReorderListKey,
  ids: readonly number[],
  saved: readonly number[]
): TReorderPending {
  const next: TReorderPending = { ...pending };
  if (sameOrder(ids, saved)) delete next[key];
  else next[key] = [...ids];
  return next;
}

/** The lists to save, categories first (their order is what the owner sees first). */
export function pendingKeys(pending: TReorderPending): TReorderListKey[] {
  const keys = Object.keys(pending).filter(
    (key): key is TReorderListKey =>
      key === 'categories' || /^cat:\d+$/.test(key)
  );
  return keys.sort((a, b) =>
    a === 'categories' ? -1 : b === 'categories' ? 1 : 0
  );
}

/** The `saveOrder` body of a list: its ids with their new 0-based indexes. */
export function orderBody(
  ids: readonly number[]
): {
  ids: number[];
  indexes: number[];
} {
  return { ids: [...ids], indexes: ids.map((_, index) => index) };
}

/** `{ id: index }` for the store after a list is saved. */
export function indexesOf(ids: readonly number[]): Record<number, number> {
  const indexes: Record<number, number> = {};
  ids.forEach((id, index) => {
    indexes[id] = index;
  });
  return indexes;
}

/**
 * A pending order against the saved list as it is now (another device may
 * have added or deleted rows): kept rows keep their pending order, new
 * ones go last.
 */
export function reconcileList(
  pendingIds: readonly number[],
  savedIds: readonly number[]
): number[] {
  const saved = new Set(savedIds);
  const kept = pendingIds.filter((id) => saved.has(id));
  const known = new Set(kept);
  return [...kept, ...savedIds.filter((id) => !known.has(id))];
}
